import axios from "axios";
import { baseUrlBackend } from "./Settings";
import {
  getSecureItem,
  removeSecureItem,
  setSecureItem,
} from "./SecureStorage";
import handleApiError from "./ApiErrorHandler";

const apiClient = axios.create({
  baseURL: baseUrlBackend,
});

// ===== Refresh Control =====
let isRefreshing = false;
let refreshSubscribers = [];

// Notify waiting requests
const onRefreshed = (newToken) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

// Add request to queue
const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

// ================= REQUEST INTERCEPTOR =================
apiClient.interceptors.request.use(
  (config) => {
    const authToken = getSecureItem("accessToken");
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ================= RESPONSE INTERCEPTOR =================
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {

      // If already refreshing → queue request
      if (isRefreshing) {
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(apiClient(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = getSecureItem("refreshToken");
        if (!refreshToken) throw new Error("No refresh token available.");

        const response = await axios.post(
          `${baseUrlBackend}auth/refresh_token`,
          {},
          {
            headers: { Authorization: `Bearer ${refreshToken}` },
          }
        );

        const newAccessToken = response.data.access_token;

        setSecureItem("accessToken", newAccessToken);

        isRefreshing = false;
        onRefreshed(newAccessToken); // retry queued requests

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);

      } catch (refreshError) {
        isRefreshing = false;

        removeSecureItem("accessToken");
        removeSecureItem("refreshToken");
        removeSecureItem("currentUser");
        removeSecureItem("role_id");
        removeSecureItem("roles");
        removeSecureItem("accessControl");
        removeSecureItem("entities");

        window.location.href = "/sign-in";

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ================= API METHODS =================
export const getRequest = (endpoint, params = {}) =>
  apiClient
    .get(endpoint, { params })
    .then((res) => res.data)
    .catch((error) => {
      handleApiError(error);
      throw error;
    });

export const postRequest = (endpoint, data = {}) =>
  apiClient
    .post(endpoint, data)
    .then((res) => res.data)
    .catch((error) => {
      handleApiError(error);
      throw error;
    });

export const postMultiPartRequest = (endpoint, data = {}) =>
  apiClient
    .post(endpoint, data, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data)
    .catch((error) => {
      handleApiError(error);
      throw error;
    });

export const putRequest = (endpoint, data = {}) =>
  apiClient
    .put(endpoint, data)
    .then((res) => res.data)
    .catch((error) => {
      handleApiError(error);
      throw error;
    });

export const deleteRequest = (endpoint, params = {}) =>
  apiClient
    .delete(endpoint, { params })
    .then((res) => res.data)
    .catch((error) => {
      handleApiError(error);
      throw error;
    });
